import { Stack } from "@mui/system";

import React from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { styled } from "@mui/material/styles";

interface DatePickerProps {
  placeholder?: string;
  dateFormat?: string;
  value?: Date;
  minDate?: Date;
  ariaLabel?: string;
  onChange?: (date: Date | null) => void;
}

export const DatePicker = ({
  placeholder,
  dateFormat,
  value,
  minDate,
  ariaLabel,
  onChange,
}: DatePickerProps) => {
  return (
    <StyledStack>
      <ReactDatePicker
        dateFormat={dateFormat}
        minDate={minDate}
        selected={value}
        aria-label={ariaLabel}
        onChange={onChange}
        placeholderText={placeholder}
        // Fixed, or a drawer's own scrolling clips the calendar and it opens
        // upward over the form instead of below the field it belongs to.
        popperPlacement="bottom-start"
        popperProps={{ strategy: "fixed" }}
      />
    </StyledStack>
  );
};

const CELL = 36;

const StyledStack = styled(Stack)(({ theme }) => {
  const { oscar } = theme.palette;

  return {
    "&& input": {
      ...theme.typography.body2,
      border: `1px solid ${oscar.light.borders.gray}`,
      borderRadius: 8,
      backgroundColor: oscar.light.surface.neutral,
      padding: "12px 16px",
      outline: "none !important",
      width: "100%",
      "&:focus": {
        borderColor: theme.palette.secondary.main,
        boxShadow: `inset 0 0 0 2px ${theme.palette.secondary.main}`,
      },
    },

    "&& .react-datepicker": {
      ...theme.typography.body2,
      border: "none",
      borderRadius: "16px",
      backgroundColor: oscar.light.surface.clear,
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
      padding: 8,
    },
    "&& .react-datepicker__triangle": { display: "none" },

    "&& .react-datepicker__header": {
      backgroundColor: "transparent",
      borderBottom: "none",
      padding: 0,
    },
    "&& .react-datepicker__current-month": {
      ...theme.typography.labelRMedium,
      color: oscar.light.content.content1,
      padding: "8px 0 12px",
    },

    "&& .react-datepicker__day-names": { margin: 0 },
    "&& .react-datepicker__day-name": {
      ...theme.typography.labelXSMedium,
      color: oscar.light.content.content2Text,
      width: CELL,
      lineHeight: `${CELL}px`,
      margin: 0,
    },
    "&& .react-datepicker__week": { display: "flex" },
    "&& .react-datepicker__day": {
      ...theme.typography.body2,
      color: oscar.light.content.content1,
      width: CELL,
      height: CELL,
      lineHeight: `${CELL}px`,
      margin: 0,
      borderRadius: "999px",
      transition: "background-color 120ms ease, color 120ms ease",
      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      "&:hover": { backgroundColor: oscar.light.surface.neutral },
    },

    "&& .react-datepicker__day--today:not(.react-datepicker__day--selected)": {
      ...theme.typography.labelRMedium,
      color: theme.palette.secondary.main,
    },
    "&& .react-datepicker__day--selected": {
      backgroundColor: theme.palette.secondary.main,
      color: oscar.light.surface.clear,
      "&:hover": { backgroundColor: theme.palette.secondary.main },
    },
    "&& .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--selected)":
      {
        backgroundColor: oscar.light.surface.neutral,
        color: oscar.light.content.content1,
      },
    "&& .react-datepicker__day--outside-month": {
      color: oscar.light.content.content3,
    },
    "&& .react-datepicker__day--disabled": {
      color: oscar.light.content.content3,
      cursor: "not-allowed",
      "&:hover": { backgroundColor: "transparent" },
    },
    "&& .react-datepicker__day:focus-visible": {
      outline: "none",
      boxShadow: `inset 0 0 0 2px ${theme.palette.secondary.main}`,
    },

    "&& .react-datepicker__navigation": {
      top: 12,
      width: 32,
      height: 32,
      borderRadius: 8,
      "&:hover": { backgroundColor: oscar.light.surface.neutral },
    },
    "&& .react-datepicker__navigation-icon::before": {
      borderColor: oscar.light.content.content2Text,
      borderWidth: "2px 2px 0 0",
      width: 7,
      height: 7,
    },
  };
});
